// ════════════════════════════════════════════════════════════════════════════
// services/sharedPost.js — the shape of a re-share, and nothing else.
//
// 🟥 THIS FILE HAS A TWIN: allo-app/services/sharedPost.ts. They must agree
//    EXACTLY. A share written on the phone is read by the browser and vice
//    versa, off one `posts` doc, with no server in between to reconcile them —
//    so a field this file spells `userImage` and the twin spells `userPhoto` is
//    a share that renders with a blank avatar on one platform only, which is
//    the hardest class of bug to see. Same reason services/callRating.js and
//    callRating.ts are twins.
//
// 🟥 AND IT IS PURE. No Firestore, no clock of its own, no uploads. It takes a
//    post and returns a plain object; the caller writes it. That is what lets
//    the test suite run BOTH twins over the same input and diff the documents
//    byte for byte, which is the only real defence against them drifting.
//
// ── WHY A RE-SHARE IS AN ORDINARY POST ──────────────────────────────────────
// 🟢 A share is a `posts` document authored by the SHARER, with the shared
//    content hanging off it as a snapshot. Not a new collection, not a flag on
//    the original. That single decision is why it needs almost no new code
//    anywhere else: the feed query already finds it, the profile grid already
//    lists it, likes and comments already work on it, moderation already sees
//    it, and the notification types already exist. The sharer's caption goes in
//    `content`, so every renderer that already shows a caption shows theirs.
//
// ── WHY THE EMBED IS A SNAPSHOT AND AN ID, NOT JUST AN ID ───────────────────
// 🟢 THE ID IS THE TRUTH; THE SNAPSHOT IS THE PAINT. Rendering a feed of 50
//    posts where a third are shares would otherwise mean 50+ extra reads before
//    anything appears — and with a chain, several per card. The snapshot lets
//    the card paint from the one document it already has, and the id is what
//    "open the original" and its live comments hang off.
//
// 🟥 THE COST IS HONEST AND WORTH NAMING: an embedded post that is later edited,
//    or whose author changes their avatar, shows stale in the card until someone
//    taps through. Facebook has exactly this behaviour for exactly this reason.
//    What must NOT go stale is deletion, which is why `openOriginal` reads live
//    and the card falls back to a tombstone.
//
// ── THE CHAIN ───────────────────────────────────────────────────────────────
// A shares X. B shares A's share. C shares B's share. The chain is KEPT rather
// than collapsed to X — that was a product decision, and it is the expensive
// one, because a chain that is kept can grow without bound and a `posts`
// document has a hard 1 MB ceiling.
//
// 🟥 SO THE DEPTH IS CAPPED IN THE DATA, NOT IN THE UI. A UI-only cap still
//    stores the whole chain, and the doc that finally exceeds 1 MB fails to
//    write with an error nobody can act on. Past MAX_EMBED_DEPTH the tail is
//    dropped and counted in `moreCount`, which the card renders as one line
//    that links to the original.
// ════════════════════════════════════════════════════════════════════════════

/** Snapshots stored BELOW the sharer's own post. 3 → the card can show the
 *  sharer, two intermediate sharers, and the original. Past that, a count. */
export const MAX_EMBED_DEPTH = 3;

/** Media items kept per snapshot. A 10-image post embedded three deep would
 *  otherwise carry 30 URLs for a card that shows at most a 2x2 grid. */
export const MAX_EMBED_MEDIA = 4;

/** Caption length on a share, matching the composer's own limit. */
export const MAX_SHARE_CAPTION = 2000;

/** Is this post a re-share of something else? */
export function isShare(post) {
  return !!(post && post.sharedFrom && post.sharedFrom.postId);
}

/**
 * 🟥 TIMES INSIDE THE EMBED ARE PLAIN MILLISECONDS, NOT Timestamps.
 *    Two reasons, both load-bearing. The value wanted is the ORIGINAL's time,
 *    so serverTimestamp() would be wrong even where it is legal. And the same
 *    snapshot shape is nested inside itself; Firestore sentinels are not
 *    allowed at arbitrary depth, and a Timestamp read back on the other client
 *    is a different class than the one written. A number means the same thing
 *    on both, forever.
 */
export function timeToMs(t) {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch (e) { return 0; } }
  if (t.seconds != null) return Number(t.seconds) * 1000;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** Only the keys that exist, so a doc never carries a field full of undefined —
 *  Firestore rejects undefined outright, and `null` everywhere makes the shapes
 *  harder to diff between the two clients. */
function withPresent(target, source, keys) {
  for (const k of keys) {
    const v = source[k];
    if (v !== undefined && v !== null && v !== '') target[k] = v;
  }
  return target;
}

const IDENTITY_KEYS = ['authorType', 'pageId', 'pageName', 'pageCategory', 'pageImage'];
const LINK_KEYS = ['linkUrl', 'linkTitle', 'linkImage', 'linkImageKind', 'linkDomain'];

/**
 * Re-cut an EXISTING embed chain to fit under a new parent.
 *
 * 🟢 The chain is not rebuilt from scratch — the post being shared already
 *    carries its own snapshot chain, correctly shaped. All that is needed is to
 *    cut it at the new depth and carry the dropped count forward, so a count
 *    that was already truncated once does not silently reset to zero.
 */
function recut(embed, depthLeft) {
  if (!embed || !embed.postId) return { inner: null, dropped: 0 };
  if (depthLeft <= 0) {
    // everything from here down is dropped: this node plus whatever it held
    return { inner: null, dropped: 1 + countChain(embed) };
  }
  const next = recut(embed.inner, depthLeft - 1);
  const node = { ...embed, inner: next.inner };
  const dropped = Number(embed.moreCount) || 0;
  if (next.dropped || dropped) node.moreCount = next.dropped + dropped;
  else delete node.moreCount;
  return { inner: node, dropped: 0 };
}

/** How many snapshots hang below this one, including any already counted. */
export function countChain(embed) {
  let n = Number(embed?.moreCount) || 0;
  let cur = embed?.inner;
  while (cur) { n += 1; n += Number(cur.moreCount) || 0; cur = cur.inner; }
  return n;
}

/**
 * Freeze one post into the embed shape.
 *
 * @param post       the post being shared, as read from Firestore
 * @param depthLeft  how many further snapshots may hang below it
 */
export function snapshotOf(post, depthLeft = MAX_EMBED_DEPTH - 1) {
  if (!post || !post.id) return null;

  const media = Array.isArray(post.media) ? post.media : [];
  const kept = media.slice(0, MAX_EMBED_MEDIA).map((m) => {
    const item = { url: String(m?.url || ''), type: m?.type === 'video' ? 'video' : 'image' };
    if (m?.id) item.id = String(m.id);
    return item;
  }).filter((m) => m.url);

  const snap = {
    postId: String(post.id),
    userId: String(post.userId || ''),
    /* 🟥 THE ORIGINAL AUTHOR'S NAME AND PICTURE ARE THE POINT OF THE FEATURE.
       They are copied here rather than looked up, because a card that has to
       wait on a profile read shows an empty byline first and fills in — which
       reads as the share having been posted by nobody. */
    userName: String(post.userName || 'Allo user'),
    userImage: String(post.userImage || ''),
    content: String(post.content || ''),
    createdAtMs: timeToMs(post.timestamp),
    mediaCount: media.length || (post.imageUrl ? 1 : 0),
  };

  if (kept.length) snap.media = kept;
  withPresent(snap, post, IDENTITY_KEYS);
  withPresent(snap, post, LINK_KEYS);
  withPresent(snap, post, ['imageUrl', 'videoUrl', 'mediaType', 'textStyle', 'locationLabel']);

  const inner = recut(post.sharedFrom, depthLeft);
  if (inner.inner) snap.inner = inner.inner;
  if (inner.dropped) snap.moreCount = inner.dropped;

  return snap;
}

/** The deepest post in the chain — the one true original, whoever shared it on. */
export function rootOf(embed) {
  let cur = embed;
  while (cur && cur.inner) cur = cur.inner;
  return cur || null;
}

/** The chain as a flat array, outermost first — what a card iterates to render. */
export function chainOf(embed) {
  const out = [];
  let cur = embed;
  while (cur && cur.postId) { out.push(cur); cur = cur.inner; }
  return out;
}

/**
 * The full document for a re-share, minus the server timestamp the caller adds.
 *
 * 🟥 THE CALLER SUPPLIES `timestamp`, NOT THIS FILE. serverTimestamp() is a
 *    client-SDK sentinel and the two SDKs are different objects; returning one
 *    from here would make the twins impossible to diff, and would drag a
 *    Firestore import into a module whose whole value is having none.
 */
export function buildSharePost({ post, me, caption = '', asPage = null }) {
  if (!post || !post.id) throw new Error('Nothing to share.');
  if (!me || !me.uid) throw new Error('Not signed in.');

  const identity = asPage ? {
    userName: asPage.name || 'Page',
    userImage: asPage.profileImage || '',
    authorType: 'page',
    pageId: asPage.id,
    pageName: asPage.name || 'Page',
    pageCategory: asPage.category || '',
    pageImage: asPage.profileImage || '',
  } : {
    userName: me.fullName || me.username || 'Allo user',
    userImage: me.profileImage || '',
  };

  const sharedFrom = snapshotOf(post, MAX_EMBED_DEPTH - 1);

  /* 🟥 THE ROOT COMES FROM THE PARENT'S OWN FLAT FIELD, NOT FROM WALKING THE
     CHAIN WE JUST CUT. The first version did `rootOf(sharedFrom)` and was wrong
     the moment truncation kicked in: once the tail is dropped, the deepest
     STORED snapshot is some intermediate sharer, not the original author — so
     "open the original" quietly started opening the wrong post, and only on the
     deep chains nobody tests by hand.

     This is the entire reason `sharedRootId` is stored flat on every share. The
     parent already knows its own root; ask it. Only when the parent is an
     ordinary post does the chain answer, and then the answer is the parent. */
  const root = String(
    post.sharedRootId || rootOf(sharedFrom)?.postId || post.id,
  );

  return {
    userId: String(me.uid),
    userEmail: me.email || '',
    ...identity,
    content: String(caption || '').slice(0, MAX_SHARE_CAPTION),
    /* A share starts its own life: its own likes, its own comments, its own
       share count. Tapping through to the embed is what reaches the original's. */
    likes: [],
    comments: [],
    sharesCount: 0,
    media: [],
    audience: 'everyone',
    commentsEnabled: true,
    textStyle: null,
    locationLabel: null,
    status: null,

    /* ── the three fields that make it a share ──────────────────────────────
       `sharedFrom`  what the card paints, chain and all.
       `sharedPostId` what was actually tapped — the immediate parent. This is
                      the one whose share counter moves, because it is the post
                      the person chose.
       `sharedRootId` the one true original at the bottom of the chain. Stored
                      flat so "how many times has this content travelled" is a
                      query rather than a walk through nested maps, which
                      Firestore cannot index. */
    sharedFrom,
    sharedPostId: String(post.id),
    sharedRootId: root,
    shareDepth: chainOf(sharedFrom).length,
  };
}

/**
 * Everyone who should hear that this was shared, deduped, minus the sharer.
 *
 * 🟢 BOTH THE PERSON WHOSE POST WAS TAPPED AND THE ORIGINAL AUTHOR. If B shares
 *    A's post and C then shares B's share, C's action is genuinely news to both:
 *    B watched their share travel, and A watched their post travel. Notifying
 *    only the original loses B entirely; notifying only the parent means the
 *    author of the thing everyone is actually looking at never hears about it.
 *    Deduped, because A and B are the same person whenever someone shares their
 *    own share, which is common.
 */
export function shareAudience({ post, sharerUid }) {
  const out = [];
  const push = (uid) => {
    const u = String(uid || '');
    if (u && u !== String(sharerUid) && !out.includes(u)) out.push(u);
  };
  push(post?.userId);
  const root = rootOf(post?.sharedFrom);
  if (root) push(root.userId);
  return out;
}
